import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { superadminApi, type EmailTemplateSummaryDto } from '@/api/superadmin'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Mail, Pencil } from 'lucide-react'

function TemplateBadge({ isCustomised }: { isCustomised: boolean }) {
  return isCustomised
    ? <Badge variant="default" className="bg-indigo-600 text-white text-xs">Custom</Badge>
    : <Badge variant="neutral" className="text-xs">Default</Badge>
}

function TemplateRow({ tmpl }: { tmpl: EmailTemplateSummaryDto }) {
  const navigate = useNavigate()
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
      <td className="py-3 px-4">
        <div className="font-medium text-sm text-slate-900">{tmpl.name}</div>
        <div className="text-xs text-slate-500 mt-0.5">{tmpl.description}</div>
      </td>
      <td className="py-3 px-4 text-sm text-slate-600 max-w-xs">
        <span className="truncate block" title={tmpl.subject}>{tmpl.subject}</span>
      </td>
      <td className="py-3 px-4">
        <TemplateBadge isCustomised={tmpl.isCustomised} />
      </td>
      <td className="py-3 px-4 text-xs text-slate-400">
        {tmpl.updatedAt ? new Date(tmpl.updatedAt).toLocaleDateString('en-GB', {
          day: 'numeric', month: 'short', year: 'numeric',
        }) : '—'}
      </td>
      <td className="py-3 px-4 text-right">
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate(`/superadmin/email-templates/${tmpl.key}`)}
          className="gap-1.5"
        >
          <Pencil size={13} />
          Edit
        </Button>
      </td>
    </tr>
  )
}

export default function EmailTemplatesListPage() {
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['superadmin', 'email-templates'],
    queryFn: () => superadminApi.getEmailTemplates().then(r => r.data),
  })

  const customCount = (templates as EmailTemplateSummaryDto[]).filter(t => t.isCustomised).length

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Mail size={20} className="text-slate-500" />
            Email Templates
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage the content of all transactional emails sent by CertSlate.
            {customCount > 0 && (
              <span className="ml-2 text-indigo-600 font-medium">
                {customCount} of {(templates as EmailTemplateSummaryDto[]).length} customised
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {isLoading ? (
          <div className="py-12 text-center text-sm text-slate-400">Loading templates…</div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Template</th>
                <th className="py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Subject</th>
                <th className="py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                <th className="py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Last updated</th>
                <th className="py-2.5 px-4" />
              </tr>
            </thead>
            <tbody>
              {(templates as EmailTemplateSummaryDto[]).map(tmpl => (
                <TemplateRow key={tmpl.key} tmpl={tmpl} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
